import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  AppBar,
  Box,
  IconButton,
  Toolbar,
  useMediaQuery,
  SvgIcon,
  useTheme,
} from '@mui/material';
import { Menu as MenuIcon } from 'lucide-react';
import { THEMES } from 'src/constants';
import Account from './Account';
import Logo from 'src/components/Logo';
import AppSwitcher from 'src/components/AppSwitcher';
import { useTranslation } from 'react-i18next';
import useSideNav from 'src/hooks/useSideNav';
import useSelectedSubAccount from 'src/hooks/useSelectedSubAccount';
import useSelectedGroup from 'src/hooks/useSelectedGroup';
import { useFlag } from '@unleash/proxy-client-react';

import useAuth from 'src/hooks/useAuth';
import { makeStyles } from 'tss-react/mui';
import { useLocation, Link as RouterLink } from 'react-router-dom';
import useGetAllSubAccountDropdownList from 'src/hooks/useGetAllSubAccountsDropdownList';

const useStyles = makeStyles()((theme) => ({
  root: {
    ...(theme.name === THEMES.LIGHT ? {
      boxShadow: 'none',
      background: theme.palette.primary.main
    } : {}),
    ...(theme.name === THEMES.ONE_DARK ? {
      background: theme.palette.background.default
    } : {})
  },
  toolbar: {
    height: 64
  },
  logo: {
    marginRight: theme.spacing(2)
  },
  logoSmall: {
    width: '50%',
  },
  autoCompleteElem: {
    marginRight: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(1.5),
    width: theme.spacing(26),
    minWidth: 80
  },
  subAccountElem: {
    width: theme.spacing(35),
    paddingRight: theme.spacing(0.5),
  },
  metalListBox: {
    background: "var(--neutral-1)",
    padding: 1
  },
  metalRoot: {
    backgroundColor: "transparent",
    boxShadow: 'none',
    paddingRight: 0
  },
  metalLogo : {
    width: '50%',
    minWidth: "150px",
    height: "40px",
    marginLeft: "16px"
  },
  metalTopBar: {
    paddingLeft: "0 !important",
    backgroundColor: 'var(--neutral-1)',
    boxShadow: "none",
    paddingRight: `${theme.spacing(2)} !important`
  },
  smallMetalTopBar: {
    backgroundColor: 'var(--neutral-1)',
    boxShadow: "none",
    paddingRight: `${theme.spacing(2)} !important`
  },
  mobileSubAccountMenu: {
      width: 250,
      overflowWrap: 'anywhere'
  },
  subAccountPopper: {
    inset: '0px auto auto -10px !important'
  }
}));

const TopBar = (props) => {
  const className = props.className
  const theme = useTheme()

  const { t }  = useTranslation();
  const { classes, cx } = useStyles();
  const { handleSelectedSubAccountUpdate, handleSelectedSubAccountGroupUpdate } = useSelectedSubAccount();
  const { selectedGroup, handleSelectedGroupUpdate, groupDataLoading } = useSelectedGroup();
  const smallMobileDevice = useMediaQuery(theme.breakpoints.down('sm'));
  const mediumMobileDevice = useMediaQuery(theme.breakpoints.down('md'));
  const { navOpen, handleSideNavUpdate, fullscreenMode } = useSideNav();
  const ssoAppSwitching = useFlag('sso_app_switching');
  const { loggedInUser } = useAuth();
  const location = useLocation();

  // Check if user has Optifleet role (from JWT token, stored in sessionStorage)
  const userRoles = JSON.parse(sessionStorage.getItem('userRoles') || '[]');
  const hasOptiFleetRole = userRoles.includes('Optifleet');
  const showAppSwitcher = ssoAppSwitching && hasOptiFleetRole;
  const selectedGroups = selectedGroup !== undefined || null ? selectedGroup.map(g => g.group) : JSON.parse(sessionStorage.getItem('selectedGroups'));
  const { data: subAccountsList } = useGetAllSubAccountDropdownList(selectedGroups);
  const [ groupNames, setGroupNames]  = useState([]);

  useEffect(() => {
    if(!selectedGroup.length){
      handleSelectedGroupUpdate(loggedInUser.groups.map(i => {
        return {
          group: {
            id: i.group.id,
            name: i.group.name,
            viewPaymentsPermission: i.defaultSubAccountRole.viewPayments
          }
        }
      }), loggedInUser.groups) //Initial Context
    }
  }, []);

  useEffect(()=>{
    let sessionGroupsValue = sessionStorage.getItem('selectedGroups') || JSON.stringify([])
    if(JSON.parse(sessionGroupsValue).length) {
      sessionGroupsValue = loggedInUser.groups.filter(i => JSON.parse(sessionGroupsValue).filter(j => i.group.id === j.group.id))
      if(loggedInUser.groups.length === sessionGroupsValue.length){
        let arr = loggedInUser.groups.filter(i => {
          return selectedGroup.some(j => {
            return j.group.id === i.group.id
          })
        })
        setGroupNames(arr.map(groupInfo => groupInfo.group.name).sort())
      }
      else {
        setGroupNames(
          JSON.parse(sessionGroupsValue).map(i => i.group.name).sort()
        )
      }
    }
    else {
      sessionStorage.setItem('selectedGroups', JSON.stringify(loggedInUser.groups.map(i => {
        return {
          group: {
            id: i.group.id,
            name: i.group.name,
            viewPaymentsPermission: i.defaultSubAccountRole.viewPayments
          }
        }
      })))
    }
    setGroupNames(loggedInUser.groups.map(groupInfo => groupInfo.group.name).sort())
  }, [loggedInUser])

  useEffect(() => {
    const r1 = /\/app\/admin\/all-sub-accounts\/\w/;
    const r2 = /\/app\/sub-accounts\/\w/;

    if(!location.pathname.match(r1) && !location.pathname.match(r2)) {
      handleSelectedSubAccountUpdate('No Sub-Account Selected')
    }
    else{
      const formatPath = location.pathname.split('/');
      const selectedSubAccount = !!location.pathname.match(r1) ? formatPath[4] : formatPath[3];
      const getGroupName = subAccountsList.length ? subAccountsList.filter(i => i.value.split(",")[0] === selectedSubAccount)[0] : null;
      if(selectedSubAccount && !!getGroupName) handleSelectedSubAccountGroupUpdate(getGroupName.groupName);
    }

  },[location, subAccountsList])

  return (
    <AppBar
      enableColorOnDark={true}
      className={cx(classes.metalRoot, className)}
    >
      <Toolbar 
        className={mediumMobileDevice ? classes.smallMetalTopBar : classes.metalTopBar}
        style={
          smallMobileDevice
            ? {paddingLeft: theme.spacing(1)}
            : {paddingLeft: theme.spacing(2)} 
          }>
          { mediumMobileDevice ? 
            <IconButton
              color="textPrimary"
              onClick={() => handleSideNavUpdate(!navOpen)}
              size="large"
              style={{ padding: 10,}}>
              <SvgIcon fontSize="small">
                <MenuIcon />
              </SvgIcon>
            </IconButton> :
            showAppSwitcher ? (
              <Box
                style={{
                  minWidth: fullscreenMode ? "auto" : "230px",
                  width: fullscreenMode ? "auto" : "230px",
                  borderRight: fullscreenMode ? "none" : "1px solid #303432",
                  height: "74px",
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "center",
                  backgroundColor: fullscreenMode ? "transparent" : "var(--neutral-2)",
                  transition: fullscreenMode ? "none" : "background-color 0s 225ms, border 0s 225ms",
                  paddingLeft: fullscreenMode ? "0" : "16px",
                  paddingRight: fullscreenMode ? "0" : undefined
                }}
              >
                <AppSwitcher />
              </Box>
            ) : (
              <RouterLink
                style={{
                  minWidth: fullscreenMode ? "auto" : "230px",
                  width: fullscreenMode ? "auto" : "230px",
                  borderRight: fullscreenMode ? "none" : "1px solid #303432",
                  height: "74px",
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "center",
                  backgroundColor: fullscreenMode ? "transparent" : "var(--neutral-2)",
                  transition: fullscreenMode ? "none" : "background-color 0s 225ms, border 0s 225ms",
                  paddingLeft: fullscreenMode ? "0" : undefined,
                  paddingRight: fullscreenMode ? "0" : undefined
                }}
                to={"/app/account-overview"}
              >
                <Logo className={classes.metalLogo}/>
              </RouterLink>
            )}
        
        <Box flexGrow={1} />

        <Box ml={2}>
          <Account/>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

TopBar.propTypes = {
  className: PropTypes.string,
  onMobileNavOpen: PropTypes.func
};

TopBar.defaultProps = {
  onMobileNavOpen: () => {}
};

export default TopBar;
